import { useContext, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash';

import { collection, orderBy, limit, onSnapshot, query, startAfter, getDocs } from 'firebase/firestore';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';

import {
  ChatData,
  ChatMessage,
  ChatMessageStatus,
  ChatMessageAttachment,
  Image,
  ChatUser,
  ChatMessageReaction,
} from '@vyce/core/src/types';
import {
  sendMessageRequest,
  getChatRequest,
  getUserChatsRequest,
  updateChatReadOnlyStatusRequest,
  searchCompanyChatUsersRequest,
  deleteMessageRequest,
  editMessageRequest,
  reactToMessageRequest,
  getMessageReactionsRequest,
  ensureUserExistsRequest,
} from '@vyce/core/src/api/chat';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';
import { useIsMounted } from '@vyce/core/src/hooks/useIsMounted';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useFirebaseAuth } from '@vyce/core/src/hooks/useFirebaseAuth';
import { db } from '@vyce/core/firebase';
import { speechToTextRequest } from '@vyce/core/src/api/ai';
import { convertFileToBase64 } from '@vyce/core/src/utils/files';
import { getFromLS, saveToLS } from '@vyce/core/src/utils/local-storage';

const CHATS_PAGE_SIZE = 40;
// First paint of a chat waits for this page, so keep it to roughly a screen
// and a half; older pages load on scroll.
export const MESSAGES_PAGE_SIZE = 50;
const VYCE_CHAT_STORAGE_KEY = 'vyce_chat';

// Instant client-side match on the chats already loaded, shown while the server search runs.
const matchesLoadedChat = (chat: ChatData, query: string) => {
  const term = query.toLowerCase().trim();
  if (!term) {
    return true;
  }
  if ((chat.name || '').toLowerCase().includes(term)) {
    return true;
  }
  return (chat.profiles || []).some(profile => (profile.name || '').toLowerCase().includes(term));
};

interface PendingMessage {
  chatId: string;
  message: ChatMessage;
  /** Id the server gave the message; set once sendMessage returns. */
  serverId?: string;
}

export const useChatData = (
  user: ChatUser,
  companyId: string,
  route: string,
  restrictedMode: boolean = false
) => {
  const [loadingChats, setLoadingChats] = useState<boolean>(false);
  const [loadingChat, setLoadingChat] = useState<boolean>(false);
  const [loadingChatUpdate, setLoadingChatUpdate] = useState<boolean>(false);
  const [chats, setChats] = useState<ChatData[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingInput, setLoadingInput] = useState<boolean>(false);
  const [selectedChat, setSelectedChat] = useState<ChatData | null>(null);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [searchedChats, setSearchedChats] = useState<ChatData[]>([]);
  const [images, setImages] = useState<File[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [vyceChat, setVyceChat] = useState<ChatData | null>(null);
  const [messageToDelete, setMessageToDelete] = useState<ChatMessage | null>(null);
  const [messageToEdit, setMessageToEdit] = useState<ChatMessage | null>(null);
  const [messageToReply, setMessageToReply] = useState<ChatMessage | null>(null);
  const [messageToForward, setMessageToForward] = useState<ChatMessage | null>(null);
  const [messageReactions, setMessageReactions] = useState<ChatMessageReaction[]>([]);
  const [loadingMessageReactions, setLoadingMessageReactions] = useState<boolean>(false);
  const [selectedReactionMessage, setSelectedReactionMessage] = useState<ChatMessage | null>(null);

  // Pagination state
  const [lastChatId, setLastChatId] = useState<string | undefined>(undefined);
  const [hasMoreChats, setHasMoreChats] = useState<boolean>(true);
  const [loadingMoreChats, setLoadingMoreChats] = useState<boolean>(false);
  const [searchingChats, setSearchingChats] = useState<boolean>(false);
  const [unreadOnlyFilter, setUnreadOnlyFilter] = useState(false);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  /** Synced in searchChats so async getChats / loadMore never clobber filtered search results. */
  const activeSearchQueryRef = useRef('');
  /** Bumped on every search or reset, so a slow response for an older query is dropped. */
  const searchRequestIdRef = useRef(0);
  /** Chat the user last asked to open; a late getChat for another chat must not replace it. */
  const requestedChatIdRef = useRef<string | null>(null);
  /** Optimistic messages not yet seen in a Firestore snapshot. */
  const pendingMessagesRef = useRef<PendingMessage[]>([]);

  const { handleServerError, showNotification } = useContext(NotificationContext);
  const { firebaseAuth } = useFirebaseAuth();
  const isMounted = useIsMounted();

  const {
    updateChat,
    uploadFileToFirebaseStorage,
    createGroupChat: createGroupChatRequest,
    addUsersToChat: addUsersToChatRequest,
    removeUsersFromChat: removeUsersFromChatRequest,
    createPrivateChat,
  } = useChatRequests(user);
  const history = useHistory();
  const isPrivate = useMemo(() => {
    return selectedChat?.type === 'private';
  }, [selectedChat]);

  const isChatAdmin = useMemo(() => {
    if (isPrivate) {
      return false;
    }
    const userId = user?.uid || '';
    const admins = selectedChat?.admins || [];
    return selectedChat?.ownerId === userId || admins.includes(userId);
  }, [selectedChat, isPrivate, user?.uid]);

  const goToChat = async (chatUser: ChatUser) => {
    if (!user || !companyId) {
      return;
    }
    await createPrivateChat({
      creator: user,
      chatUser,
      companyId,
    });
  };

  const ensureUserExists = async () => {
    try {
      const data = await ensureUserExistsRequest(user);
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  const createGroupChat = async ({ chatName, users }: { chatName: string; users: ChatUser[] }) => {
    try {
      const data = await createGroupChatRequest({
        creator: user,
        participants: users,
        companyId,
        name: chatName,
      });
      getChats(user.uid, true);
      if (data) {
        history.push(`${route}?chatId=${data}&companyId=${companyId}`);
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const searchChatUsers = async (searchQuery: string, limit: number = 10) => {
    try {
      const data = await searchCompanyChatUsersRequest({
        companyId,
        searchQuery,
        limit,
      });
      return data.users.filter(it => it.uid !== user?.uid);
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const getVyceChat = useCallback(async (uuid: string) => {
    if (uuid === 'vyce') {
      return null;
    }
    try {
      const storedChat = getFromLS(VYCE_CHAT_STORAGE_KEY);

      if (storedChat && isMounted.current) {
        setVyceChat(storedChat);
      }
      const data = await getUserChatsRequest({
        uid: uuid,
        companyId: 'vyce',
        limit: 1,
      });
      const chat = data.chats[0];
      if (!chat) {
        return null;
      }
      chat.name = 'Vyce Notifications';
      saveToLS(VYCE_CHAT_STORAGE_KEY, chat);
      return chat;
    } catch (e) {
      console.error(e);
      return null;
    }
  }, []);

  const getChats = useCallback(
    async (userId: string, resetPagination: boolean = true, unreadOnlyOverride?: boolean) => {
      // Restricted mode (no messaging subscription): only the Vyce Notifications chat is available
      if (restrictedMode) {
        setLoadingChats(true);
        setHasMoreChats(false);
        try {
          await ensureUserExists();
          const vyceChat = await getVyceChat(userId);
          if (!isMounted.current) return;
          const restrictedChats = vyceChat ? [vyceChat] : [];
          setChats(restrictedChats);
          setSearchedChats(restrictedChats);
        } catch (e) {
          console.error(e);
        } finally {
          if (isMounted.current) setLoadingChats(false);
        }
        return;
      }

      const unreadOnly = unreadOnlyOverride ?? unreadOnlyFilter;

      if (resetPagination) {
        setLoadingChats(true);
        setLastChatId(undefined);
        setHasMoreChats(true);
      } else {
        setLoadingMoreChats(true);
      }

      try {
        const currentLastChatId = resetPagination ? undefined : lastChatId;
        // Two independent calls: run them side by side, not one after the other.
        const [data, vyceChat] = await Promise.all([
          getUserChatsRequest({
            uid: userId,
            companyId,
            limit: CHATS_PAGE_SIZE,
            slim: true,
            lastChatId: currentLastChatId,
            unreadOnly,
          }),
          resetPagination ? getVyceChat(userId) : Promise.resolve(null),
        ]);
        if (!isMounted.current) return;
        let newChats = [...data.chats];

        if (vyceChat && resetPagination) {
          const vyceUnread =
            (vyceChat.unreadCounts?.[userId] ?? vyceChat.unreadCount ?? 0) > 0 || vyceChat.hasUnread;
          if (!unreadOnly || vyceUnread) {
            newChats.unshift(vyceChat);
          }
        }

        if (resetPagination) {
          setChats(newChats);
          if (!activeSearchQueryRef.current.trim()) {
            setSearchedChats(newChats);
          }
        } else {
          setChats(prev => [...prev, ...newChats]);
          if (!activeSearchQueryRef.current.trim()) {
            setSearchedChats(prev => [...prev, ...newChats]);
          }
        }

        if (data.chats.length > 0) {
          setLastChatId(data.lastChatId ?? data.chats[data.chats.length - 1].id);
        } else if (resetPagination) {
          setLastChatId(undefined);
        }

        setHasMoreChats(data.hasMore ?? data.chats.length === CHATS_PAGE_SIZE);

        if (resetPagination) {
          setLoadingChats(false);
        } else {
          setLoadingMoreChats(false);
        }
      } catch (e) {
        if (isMounted.current) {
          // A failed page must not leave the list asking for the next one,
          // or the last row's observer requests it again on every render.
          setHasMoreChats(false);
          if (resetPagination) {
            setLoadingChats(false);
          } else {
            setLoadingMoreChats(false);
          }
        }
        console.error(e);
      }
    },
    [companyId, lastChatId, getVyceChat, unreadOnlyFilter, restrictedMode]
  );

  const loadMoreChats = async (userId: string) => {
    if (restrictedMode) {
      return;
    }
    if (activeSearchQueryRef.current.trim()) {
      return;
    }
    // Without a cursor a "next page" request would fetch page one again.
    if (!hasMoreChats || loadingMoreChats || !lastChatId) {
      return;
    }
    await getChats(userId, false);
  };

  /**
   * Opens a chat. With `preloaded` (the chat's row from the list) the chat shows at once
   * and the full record (complete member profiles) replaces it in the background.
   */
  const getChat = async (chatId: string, userId: string, chatCompanyId: string, preloaded?: ChatData) => {
    requestedChatIdRef.current = chatId;
    if (preloaded) {
      setSelectedChat(preloaded);
    } else {
      setLoadingChat(true);
    }
    try {
      const data = await getChatRequest({ chatId, userId, companyId: chatCompanyId });
      if (!isMounted.current || requestedChatIdRef.current !== chatId) return;
      // The list renames the Vyce chat to "Vyce Notifications"; keep the header in step with it.
      const keepListName = preloaded && preloaded.ownerId === 'vyce' && userId !== 'vyce';
      setSelectedChat(keepListName ? { ...data, name: preloaded!.name } : data);
    } catch (e) {
      console.error(e);
    } finally {
      if (isMounted.current && !preloaded) setLoadingChat(false);
    }
  };

  const performSearch = useCallback(
    async (query: string, unreadOnlyOverride?: boolean) => {
      const unreadOnly = unreadOnlyOverride ?? unreadOnlyFilter;
      const requestId = ++searchRequestIdRef.current;
      setSearchingChats(true);
      try {
        const [data, vyceChat] = await Promise.all([
          getUserChatsRequest({
            uid: user.uid,
            companyId,
            limit: CHATS_PAGE_SIZE,
            slim: true,
            searchQuery: query,
            unreadOnly,
          }),
          getVyceChat(user.uid),
        ]);
        // The user has typed on (or cleared the field) since this request went out.
        if (!isMounted.current || requestId !== searchRequestIdRef.current) return;
        let searchResults = [...data.chats];

        if (vyceChat && vyceChat.name?.toLowerCase().includes(query.toLowerCase())) {
          const vyceUnread =
            (vyceChat.unreadCounts?.[user.uid] ?? vyceChat.unreadCount ?? 0) > 0 || vyceChat.hasUnread;
          if (!unreadOnly || vyceUnread) {
            searchResults.unshift(vyceChat);
          }
        }

        setSearchedChats(searchResults);
        setHasMoreChats(data.hasMore ?? false);
      } catch (e) {
        console.error('Error searching chats:', e);
      } finally {
        if (isMounted.current && requestId === searchRequestIdRef.current) setSearchingChats(false);
      }
    },
    [companyId, user.uid, getVyceChat, unreadOnlyFilter]
  );

  const toggleUnreadFilter = useCallback(() => {
    const next = !unreadOnlyFilter;
    setUnreadOnlyFilter(next);
    if (activeSearchQuery.trim()) {
      performSearch(activeSearchQuery, next);
    } else {
      getChats(user.uid, true, next);
    }
  }, [unreadOnlyFilter, activeSearchQuery, getChats, user.uid, performSearch]);

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        performSearch(query);
      }, 300),
    [performSearch]
  );

  const searchChats = useCallback(
    (query: string) => {
      activeSearchQueryRef.current = query;
      setActiveSearchQuery(query);
      if (!query) {
        debouncedSearch.cancel();
        searchRequestIdRef.current++;
        setSearchingChats(false);
        getChats(user.uid, true);
        return;
      }

      // Show matches among the chats already loaded straight away; the server
      // search (which also covers chats not loaded yet) replaces them when it lands.
      setSearchedChats(chats.filter(chat => matchesLoadedChat(chat, query)));
      debouncedSearch(query);
    },
    [user.uid, debouncedSearch, getChats, chats]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const updateData = async (chatId: string) => {
    getChat(chatId, user.uid, companyId);
    getChats(user.uid, true); // Reset pagination when updating data
  };

  const updateChatName = async (chatName: string) => {
    setLoadingChatUpdate(true);
    try {
      if (!selectedChatId || !companyId || !chatName) {
        return;
      }

      await updateChat({
        companyId,
        chatId: selectedChatId,
        name: chatName,
        avatarUrl: selectedChat?.avatarUrl ?? '',
        userId: user.uid,
      });
      updateData(selectedChatId);
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoadingChatUpdate(false);
    }
  };

  const updateChatAvatar = async (avatar: Image) => {
    setLoadingChatUpdate(true);
    try {
      if (!avatar.file || !selectedChatId) {
        return;
      }
      const imageUrl = await uploadFileToFirebaseStorage(avatar.file);
      if (imageUrl) {
        await updateChat({
          companyId,
          chatId: selectedChatId,
          avatarUrl: imageUrl,
          userId: user.uid,
        });
        updateData(selectedChatId);
      }
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoadingChatUpdate(false);
    }
  };

  const updateChatReadOnlyStatus = async (readOnly: boolean) => {
    setLoadingChatUpdate(true);
    if (!selectedChatId) {
      return;
    }
    try {
      await updateChatReadOnlyStatusRequest({
        chatId: selectedChatId,
        readOnly,
        companyId,
        userId: user.uid,
      });
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoadingChatUpdate(false);
    }
  };

  const listenToMessages = async (
    companyId: string,
    chatId: string,
    callback: (messages: ChatMessage[]) => void
  ) => {
    try {
      if (!companyId || !chatId || !user.uid) {
        console.error('Missing required parameters for listenToMessages:', {
          companyId,
          chatId,
          userId: user.uid,
        });
        return () => {};
      }

      const token = await firebaseAuth({
        email: user.email,
        phone: user.phone,
        userId: user.uid,
      });

      if (!token) {
        console.error('Failed to authenticate with Firebase');
        return () => {};
      }

      const messagesRef = collection(db, 'companies', companyId, 'chats', chatId, 'messages');
      const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(MESSAGES_PAGE_SIZE));

      const unsubscribe = onSnapshot(
        q,
        snapshot => {
          const messages: ChatMessage[] = snapshot?.docs
            ? snapshot?.docs?.map(doc => ({
                id: doc.id,
                ...(doc.data() as Omit<ChatMessage, 'id'>),
              }))
            : [];
          callback(messages);
        },
        error => {
          console.error('Firestore listener error:', error);
          if (error.code === 'permission-denied') {
            console.error('Permission denied: User may not have access to this chat or company');
            console.error('User ID:', user.uid, 'Company ID:', companyId, 'Chat ID:', chatId);
          }
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up Firestore listener:', error);
      return () => {};
    }
  };

  const loadMoreMessages = useCallback(
    async (chatId: string, companyId: string) => {
      if (!user?.uid || messages.length < MESSAGES_PAGE_SIZE) {
        return;
      }

      try {
        const messagesRef = collection(db, 'companies', companyId, 'chats', chatId, 'messages');

        // With reversed scroll, the oldest message is at the end of the array
        const oldestMessage = messages[messages.length - 1];
        const oldestTimestamp = oldestMessage.createdAt;

        const q = query(
          messagesRef,
          orderBy('createdAt', 'desc'),
          limit(MESSAGES_PAGE_SIZE),
          startAfter(oldestTimestamp)
        );

        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          return;
        }

        const newMessages: ChatMessage[] = snapshot.docs.map((doc: any) => ({
          id: doc.id,
          ...(doc.data() as Omit<ChatMessage, 'id'>),
        }));

        setMessages(prev => [...prev, ...newMessages]);
      } catch (error) {
        console.error('Error loading more messages:', error);
      }
    },
    [messages, user?.uid]
  );

  const speechToText = async (audioFile: string) => {
    if (!selectedChatId) {
      return;
    }
    try {
      setLoadingInput(true);
      const res = await speechToTextRequest(audioFile);
      const message = res.data.body;
      setLoadingInput(false);
      return message;
    } catch (e) {
      setLoadingInput(false);
      handleServerError(e);
      return '';
    }
  };

  const deleteDocument = (fileName: string) => {
    setDocuments(prev => prev.filter(f => f.name !== fileName));
  };

  const uploadDocument = (file: File) => {
    setDocuments(prev => [...prev, file]);
  };

  const deleteImage = (imageName: string) => {
    setImages(prev => prev.filter(f => f.name !== imageName));
  };

  const uploadImage = (image: File) => {
    setImages(prev => [...prev, image]);
  };

  // The Vyce Notifications chat lives in the 'vyce' namespace, not the company's one
  const selectedChatCompanyId = selectedChat?.companyId ?? companyId;

  const sendMessage = async (text: string) => {
    if (!user?.uid || !selectedChatId) {
      return;
    }
    try {
      const imagesAttachments = await Promise.all(images.map(async image => convertFileToBase64(image)));
      const documentsAttachments: ChatMessageAttachment[] = await Promise.all(
        documents?.map(async attachment => convertFileToBase64(attachment))
      );

      const message: ChatMessage = {
        localId: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        senderId: user.uid,
        senderName: user.name,
        senderAvatar: user.avatarUrl || '',
        text,
        images: imagesAttachments,
        documents: documentsAttachments,
        createdAt: { seconds: dayjs().unix() },
        status: ChatMessageStatus.SENT,
        replyTo: {
          messageId: messageToReply?.id ?? '',
          ...messageToReply,
        },
      };
      const pending: PendingMessage = { chatId: selectedChatId, message };
      pendingMessagesRef.current = [pending, ...pendingMessagesRef.current];
      setMessages(messages => [message, ...messages]);
      setMessageToReply(null);
      try {
        const result = (await sendMessageRequest({
          chatId: selectedChatId,
          sender: user,
          text,
          images: imagesAttachments || [],
          documents: documentsAttachments || [],
          companyId: selectedChatCompanyId,
          replyingMessageId: messageToReply?.id,
        })) as { messageId?: string } | undefined;
        if (!result?.messageId) {
          dropPendingMessage(message.localId!);
          return;
        }
        pending.serverId = result.messageId;
        // The snapshot carrying the server copy usually lands before this response;
        // otherwise the next snapshot retires the bubble (withPendingMessages).
        setMessages(prev => {
          if (!prev.some(item => item.id === result.messageId)) {
            return prev;
          }
          dropPendingMessage(message.localId!);
          return prev.filter(item => item.localId !== message.localId);
        });
      } catch (e) {
        dropPendingMessage(message.localId!);
        setMessages(prev => prev.filter(item => item.localId !== message.localId));
        showNotification({ message: 'Message not sent. Please try again.', options: { variant: 'error' } });
        throw e;
      }
    } catch (e) {
      console.error(e);
    }
  };

  const dropPendingMessage = (localId: string) => {
    pendingMessagesRef.current = pendingMessagesRef.current.filter(item => item.message.localId !== localId);
  };

  /**
   * Puts optimistic messages back on top of a Firestore snapshot. A snapshot replaces the
   * whole list, so without this a just-sent bubble vanishes on any unrelated update
   * (a read receipt, a reaction) and reappears only when the server copy arrives.
   */
  const withPendingMessages = (chatId: string, snapshot: ChatMessage[]) => {
    const ids = new Set(snapshot.map(item => item.id));
    // Before sendMessage returns we don't know the server id yet; the server copy is
    // then recognised as our own message with the same text, written after the bubble.
    const echoed = (item: PendingMessage) =>
      item.serverId
        ? ids.has(item.serverId)
        : snapshot.some(
            server =>
              server.senderId === item.message.senderId &&
              (server.text || '') === (item.message.text || '') &&
              (server.createdAt?.seconds ?? 0) >= (item.message.createdAt?.seconds ?? 0) - 5
          );
    pendingMessagesRef.current = pendingMessagesRef.current.filter(item => !echoed(item));
    const pending = pendingMessagesRef.current
      .filter(item => item.chatId === chatId)
      .map(item => item.message);
    return pending.length ? [...pending, ...snapshot] : snapshot;
  };

  const addUsersToChat = async (users: ChatUser[]) => {
    if (!selectedChatId) {
      return;
    }
    try {
      await addUsersToChatRequest({
        companyId,
        chatId: selectedChatId,
        users,
      });
      updateData(selectedChatId);
    } catch (e) {
      console.error(e);
    }
  };

  const removeUserFromChat = async (userId: string) => {
    if (!selectedChatId) {
      return;
    }
    try {
      await removeUsersFromChatRequest({
        companyId,
        chatId: selectedChatId,
        userIds: [userId],
      });
      updateData(selectedChatId);
    } catch (e) {
      console.error(e);
    }
  };

  const deleteMessage = async (chatId: string, messageId: string) => {
    if (!user?.uid || !companyId) {
      return;
    }
    try {
      setMessages(prev => prev.filter(message => message.id !== messageId));
      await deleteMessageRequest({ chatId, messageId, companyId: selectedChatCompanyId, userId: user.uid });
    } catch (e) {
      console.error(e);
    }
  };

  const editMessage = async (chatId: string, messageId: string, newText: string) => {
    if (!user?.uid || !companyId) {
      return;
    }
    try {
      const newMessages = messages.map(message => {
        if (message.id === messageId) {
          return { ...message, text: newText, edited: true };
        }
        return message;
      });
      setMessages(newMessages);
      setMessageToEdit(null);
      await editMessageRequest({
        chatId,
        messageId,
        newText,
        companyId: selectedChatCompanyId,
        userId: user.uid,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const forwardMessage = async (recipients: ChatData[], message: string) => {
    if (!companyId || !messageToForward?.id || !selectedChatId) {
      return;
    }
    try {
      const promises = recipients.map(async recipient => {
        await sendMessageRequest({
          chatId: recipient.id,
          sender: user,
          text: messageToForward.text,
          images: messageToForward.images || [],
          documents: messageToForward.documents || [],
          companyId,
          forwardedFrom: {
            chatId: selectedChatId,
            messageId: messageToForward.id || '',
            chatName: messageToForward.senderName,
          },
        });
        if (message) {
          await sendMessage(message);
        }
      });

      await Promise.all(promises);
    } catch (error) {
      console.error(error);
    }
  };

  const isUserReactedToMessage = (messageId: string, emoji: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message) {
      return false;
    }
    return message.reactions?.some(r => r.emoji === emoji && r.userIds?.some(u => u === user?.uid));
  };

  const reactToMessage = async ({
    chatId,
    messageId,
    emoji,
    companyId,
  }: {
    chatId: string;
    messageId: string;
    emoji: string;
    companyId: string;
  }) => {
    if (!user?.uid || !companyId) {
      return;
    }
    try {
      const action = isUserReactedToMessage(messageId, emoji) ? 'remove' : 'add';

      const newMessages = messages.map(message => {
        if (message.id === messageId) {
          const existingReaction = message.reactions?.find(r => r.emoji === emoji);
          const updatedReactions = message.reactions || [];

          if (action === 'add') {
            if (existingReaction) {
              if (!existingReaction.userIds?.some(u => u === user.uid)) {
                existingReaction.userIds?.push(user.uid);
              }
            } else {
              updatedReactions.unshift({
                emoji,
                userIds: [user.uid],
                count: 1,
              });
            }
          } else {
            if (existingReaction) {
              existingReaction.userIds = existingReaction.userIds?.filter(u => u !== user.uid);
              if (existingReaction.userIds?.length === 0) {
                return {
                  ...message,
                  reactions: updatedReactions.filter(r => r.emoji !== emoji),
                };
              }
            }
          }

          return {
            ...message,
            reactions: updatedReactions,
          };
        }
        return message;
      });

      setMessages(newMessages);
      await reactToMessageRequest({ chatId, messageId, companyId, user, emoji, action });
    } catch (e) {
      console.error(e);
    }
  };

  const getMessageReactions = async (messageId: string) => {
    if (!user?.uid || !companyId || !selectedChatId) {
      return;
    }
    try {
      setLoadingMessageReactions(true);
      const result = await getMessageReactionsRequest({
        companyId: selectedChatCompanyId,
        chatId: selectedChatId,
        messageId,
        userId: user?.uid,
      });
      setMessageReactions(result.reactions || []);
      setLoadingMessageReactions(false);
    } catch (error) {
      console.error(error);
      setLoadingMessageReactions(false);
    }
  };

  return {
    getChats,
    chats,
    loadingChats,
    getChat,
    selectedChat,
    loadingChat,
    selectedChatId,
    setSelectedChatId,
    searchedChats,
    searchChats,
    setSearchedChats,
    setSelectedChat,
    updateChatName,
    loadingChatUpdate,
    updateChatAvatar,
    isChatAdmin,
    updateChatReadOnlyStatus,
    listenToMessages,
    messages,
    setMessages,
    withPendingMessages,
    loadMoreMessages,
    setLoadingChat,
    speechToText,
    loadingInput,
    sendMessage,
    setChats,
    documents,
    deleteDocument,
    uploadDocument,
    setDocuments,
    createGroupChat,
    searchChatUsers,
    vyceChat,
    getVyceChat,
    addUsersToChat,
    removeUserFromChat,
    goToChat,
    images,
    deleteImage,
    uploadImage,
    setImages,
    deleteMessage,
    editMessage,
    messageToDelete,
    setMessageToDelete,
    messageToEdit,
    setMessageToEdit,
    messageToReply,
    setMessageToReply,
    messageToForward,
    setMessageToForward,
    forwardMessage,
    reactToMessage,
    getMessageReactions,
    messageReactions,
    loadingMessageReactions,
    selectedReactionMessage,
    setSelectedReactionMessage,
    ensureUserExists,
    loadMoreChats,
    hasMoreChats,
    loadingMoreChats,
    searchingChats,
    unreadOnlyFilter,
    toggleUnreadFilter,
  };
};
